"use client"

import { motion } from "motion/react"
import { ArrowRight, Brain, Briefcase, Code, Database } from "lucide-react"
import Link from "next/link"
import { useState } from "react"

import Magnet from "@/core/ui/effects/Magnet"
import SpotlightCard from "@/core/ui/effects/SpotlightCard"
import { getServiceTheme } from "@/core/utils/service-theme"
import { serviceCategories } from "@/data/services"

export default function CategorySelector() {
  const [_hoveredCategory, setHoveredCategory] = useState<string | null>(null)

  const getCategoryIcon = (iconName: string) => {
    switch (iconName) {
      case "Database": return Database
      case "Brain": return Brain
      case "Code": return Code
      case "Briefcase": return Briefcase
      default: return Database
    }
  }

  // Tech-themed abstract descriptions
  const techDescriptions: Record<string, string> = {
    blockchain: "Decentralized consensus systems and cryptographic ledgers.",
    genai: "Neural networks, LLMs, and adaptive machine intelligence.",
    development: "Full-cycle engineering for scalable digital ecosystems.",
    enterprise: "High-availability architecture for mission-critical operations.",
  }

  return (
    <div id="services-grid" className="py-32 relative z-10 bg-background transition-colors duration-300">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="text-cyan-500 font-mono text-sm tracking-widest uppercase mb-4 block"
            >
              {"// SYSTEM_MODULES"}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold text-foreground leading-tight"
            >
              Strategic <span className="text-muted-foreground">Domains</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground max-w-md text-right hidden md:block"
          >
            Select a vector to initialize detailed protocol specifications.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {serviceCategories.map((category, index) => {
            const Icon = getCategoryIcon(category.icon)
            const theme = getServiceTheme(category.id)

            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredCategory(category.id)}
                onMouseLeave={() => setHoveredCategory(null)}
                className="h-[360px]"
              >
                <Link href={`/services/${category.id}`} className="block h-full group">
                  <SpotlightCard
                    className={`h-full bg-card/30 border-border/10 relative overflow-hidden flex flex-col justify-between p-8 md:p-10 hover:${theme.accentBorder} transition-colors duration-500`}
                    spotlightColor={`${theme.hex}26`}
                  >
                    {/* Background Massive Icon */}
                    <Icon className="absolute -right-12 -bottom-12 w-64 h-64 text-foreground/2 rotate-[-15deg] group-hover:rotate-0 group-hover:scale-110 transition-transform duration-700 ease-out pointer-events-none" />

                    {/* Header */}
                    <div className="relative z-10 flex justify-between items-start">
                      <div className={`w-14 h-14 rounded-2xl bg-muted/40 border border-border/20 flex items-center justify-center group-hover:${theme.accentBg} group-hover:${theme.accentBorder} transition-colors duration-300`}>
                        <Icon className={`w-6 h-6 text-foreground group-hover:${theme.accentColor}`} />
                      </div>
                      <span className="font-mono text-xs text-muted-foreground/30 tracking-widest">
                        0{index + 1}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <h3 className={`text-3xl font-bold text-foreground mb-3 group-hover:${theme.accentColor} transition-colors`}>
                        {category.translations.en.title}
                      </h3>
                      <p className="text-muted-foreground max-w-sm mb-8 leading-relaxed group-hover:text-foreground/80 transition-colors">
                        {techDescriptions[category.id] || category.translations.en.description}
                      </p>

                      <div className="flex items-center">
                        <Magnet padding={20} disabled={false} magnetStrength={3}>
                          <div className={`flex items-center gap-2 text-sm font-medium text-foreground group-hover:${theme.accentColor} transition-colors`}>
                            <span className="uppercase tracking-widest text-xs">Initialize</span>
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </div>
                        </Magnet>
                      </div>
                    </div>
                  </SpotlightCard>
                </Link>
              </motion.div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
